import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseVoiceSessionReturn } from '@orborix/voice-client';

/**
 * Modo charla a manos libres del composer: el mic queda ABIERTO entre turnos
 * y el server cierra cada turno solo por VAD (`captureMode: 'free'`, el mismo
 * modo con el que vive speak). Es un MODO de la conversación, no un gesto más
 * del push-to-talk: se entra con el botón de ondas del composer y se sale con
 * la X de la barra viva.
 *
 * El toggle usa `session.update {captureMode}` — el backend lo aplica en
 * vivo al SttProcessor sin tirar la sesión ni el contexto
 * (buildWsEventHandlers.onSessionUpdate). Al salir volvemos a 'hold' Y
 * pausamos la captura client-side, el invariante del push-to-talk.
 *
 * Mientras el modo está activo, `useHoldTurn` debe ir `suspended` (su guard
 * de "mic cerrado entre turnos" pausaría la captura en cada transición del
 * pipeline y mataría el modo).
 */
export interface FreeChat {
  /** Modo charla activo (la UI muestra la barra viva en vez del composer). */
  active: boolean;
  /** Mic silenciado sin salir del modo (toggle de la barra). */
  muted: boolean;
  /** Entra al modo: captureMode 'free' + captura abierta. */
  enter: () => void;
  /** Sale del modo: captureMode 'hold' + captura pausada (vuelve el PTT). */
  exit: () => void;
  /** Silencia / reactiva el mic sin salir del modo. */
  toggleMute: () => void;
  /**
   * Duck del mic mientras suena audio LOCAL de la pantalla (replay de una
   * burbuja, preview de una voz): con el mic abierto, el VAD server-side toma
   * ese audio como un turno del user — facturado, y la IA respondiéndole a su
   * propio eco. NO toca el flag `muted` (es del user): al soltar el duck la
   * captura vuelve al estado real (abierta salvo mute). Fuera del modo es
   * inerte — ahí la captura la maneja el push-to-talk.
   */
  setDucked: (ducked: boolean) => void;
  /** RMS instantáneo del mic [0..1] para la waveform (leer desde RAF). */
  getEnergy: () => number;
}

export function useFreeChat(session: UseVoiceSessionReturn): FreeChat {
  // `session` cambia de referencia por render; ref estable para los handlers.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  activeRef.current = active;
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  mutedRef.current = muted;
  // Duck por audio local sonando: sólo ref — la barra viva no lo muestra (es
  // un estado transitorio de milisegundos a segundos, no una decisión del user).
  const duckedRef = useRef(false);

  /**
   * Estado REAL de la captura dentro del modo: abierta salvo mute o duck. Fuera
   * del modo no toca nada — ahí la captura la maneja el push-to-talk
   * (`useHoldTurn`) y pisarla mataría un hold en curso.
   */
  const syncCapture = useCallback((): void => {
    if (!activeRef.current) return;
    const s = sessionRef.current;
    if (mutedRef.current || duckedRef.current) s.pauseCapture();
    else s.resumeCapture();
  }, []);

  const enter = useCallback((): void => {
    const s = sessionRef.current;
    // Barge-in: entrar al modo con la voz viva hablando la corta (mismo
    // criterio que useHoldTurn.start — tomar la palabra interrumpe la voz
    // half-duplex).
    if (s.state === 'speaking' || s.isAudioPlaying) void s.interrupt();
    if (s.paused) s.resumeSession();
    s.sendSessionUpdate({ captureMode: 'free' });
    // Los refs se escriben ANTES de sincronizar: el estado recién llega en el
    // próximo render y `syncCapture` lee refs.
    activeRef.current = true;
    mutedRef.current = false;
    setActive(true);
    setMuted(false);
    syncCapture();
  }, [syncCapture]);

  const exit = useCallback((): void => {
    const s = sessionRef.current;
    s.pauseCapture();
    s.sendSessionUpdate({ captureMode: 'hold' });
    activeRef.current = false;
    mutedRef.current = false;
    setActive(false);
    setMuted(false);
  }, []);

  const toggleMute = useCallback((): void => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
    syncCapture();
  }, [syncCapture]);

  const setDucked = useCallback(
    (ducked: boolean): void => {
      duckedRef.current = ducked;
      syncCapture();
    },
    [syncCapture],
  );

  // Reconexión con el modo activo (blip de red / reattach): el server
  // RECONSTRUYE el pipeline y re-aplica el `?captureMode=hold` del query antes
  // de arrancarlo (startVoiceSession aplica el inicial), así que renace en
  // 'hold' mientras la barra viva sigue diciendo "te escucho" — reabrimos la
  // captura al volver a 'open' y re-afirmamos 'free' con el server listo. El mismo
  // mecanismo hace innecesario revertir a 'hold' al desmontar: la próxima
  // visita reconecta y el query param ya la arranca en 'hold'.
  //
  // El pedido de 'free' va cuando el server avisa que está listo
  // (`orborix:session.started`, también tras reattach): lo mandado al abrir el
  // socket se pierde, porque su transport recién escucha al terminar de
  // arrancar (mismo motivo que el `mode.switch` de `handleSessionStarted`).
  // Sin esto el mic quedaba abierto y el server en 'hold' esperando un soltar
  // que nunca llega — «no termina de escuchar nunca» (Emmanuel, 14/10).
  const { connectionStatus } = session;
  const prevStatusRef = useRef(connectionStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = connectionStatus;
    if (!activeRef.current) return;
    if (connectionStatus !== 'open' || prev === 'open') return;
    syncCapture();
  }, [connectionStatus, syncCapture]);
  useEffect(() => {
    const onServerReady = (): void => {
      if (!activeRef.current) return;
      sessionRef.current.sendSessionUpdate({ captureMode: 'free' });
      syncCapture();
    };
    window.addEventListener('orborix:session.started', onServerReady);
    return () => window.removeEventListener('orborix:session.started', onServerReady);
  }, [syncCapture]);

  const getEnergy = useCallback((): number => sessionRef.current.getMicEnergy(), []);

  return useMemo<FreeChat>(
    () => ({ active, muted, enter, exit, toggleMute, setDucked, getEnergy }),
    [active, muted, enter, exit, toggleMute, setDucked, getEnergy],
  );
}
